import { useState } from 'react';
import { App, Button, Flex, Segmented, Space } from 'antd';
import TextArea from 'antd/es/input/TextArea';

type Mode = 'text' | 'json';

export function isJsonLike(s: string): boolean {
  const t = s?.trim?.() ?? '';
  if (!t) return false;
  if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
    try {
      JSON.parse(t);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

export default function JsonValueField(props: {
  value: string;
  onChange: (next: string) => void;
  autoDetect?: boolean;
}) {
  const { value, onChange, autoDetect = true } = props;
  const { message } = App.useApp();
  const [mode, setMode] = useState<Mode>(() => (autoDetect && isJsonLike(value) ? 'json' : 'text'));

  function toPretty() {
    try {
      const pretty = JSON.stringify(JSON.parse(value), null, 2);
      onChange(pretty);
    } catch {
      message.error('Value is not valid JSON');
    }
  }

  function validateJson() {
    try {
      JSON.parse(value);
      message.success('Valid JSON');
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Invalid JSON');
    }
  }

  return (
    <Space.Compact direction="vertical" style={{ width: '100%' }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
        <Segmented<Mode>
          options={[
            { label: 'Text', value: 'text' },
            { label: 'JSON', value: 'json' },
          ]}
          value={mode}
          onChange={(v) => setMode(v as Mode)}
        />
        {mode === 'json' && (
          <Space>
            <Button size="small" onClick={validateJson}>
              Validate
            </Button>
            <Button size="small" onClick={toPretty}>
              Format
            </Button>
          </Space>
        )}
      </Flex>

      <TextArea
        autoSize={{ minRows: 4, maxRows: 12 }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={mode === 'json' ? '{ "k": "v" } or [ ... ]' : 'String value'}
      />
    </Space.Compact>
  );
}
